import {
  PushNotifications,
  Token,
  PushNotificationSchema,
  ActionPerformed
} from '@capacitor/push-notifications'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { apiInstance } from 'services/api'

// Persisted so that unregister works after an app restart, when the
// in-memory token from the registration listener is gone.
const PUSH_TOKEN_STORAGE_KEY = 'pushToken_r4r'

let currentToken: string | null = null

const persistToken = async (token: string | null) => {
  currentToken = token
  if (token === null) {
    await Preferences.remove({ key: PUSH_TOKEN_STORAGE_KEY })
  } else {
    await Preferences.set({ key: PUSH_TOKEN_STORAGE_KEY, value: token })
  }
}

const loadPersistedToken = async (): Promise<string | null> => {
  if (currentToken) {
    return currentToken
  }
  const { value } = await Preferences.get({ key: PUSH_TOKEN_STORAGE_KEY })
  currentToken = value
  return value
}

// Resolves to false when push is unavailable (web) or the user denied the
// OS-level permission, so callers (e.g. the consents toggle) can reflect it.
export const initPushNotifications = async (accessToken: string): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    return false
  }

  let permStatus = await PushNotifications.checkPermissions()
  if (permStatus.receive === 'prompt') {
    permStatus = await PushNotifications.requestPermissions()
  }
  if (permStatus.receive !== 'granted') {
    console.log('Push notifications permission denied')
    return false
  }

  // init may run more than once per app session (e.g. re-login) — drop the
  // previous listeners so registration is not reported multiple times.
  await PushNotifications.removeAllListeners()

  PushNotifications.addListener('registration', async (token: Token) => {
    await persistToken(token.value)
    const platform = Capacitor.getPlatform()

    try {
      await apiInstance({
        url: '/notifications/device-tokens/',
        method: 'POST',
        headers: { authorization: `Bearer ${accessToken}` },
        data: { token: token.value, platform }
      })
      console.log('Device token registered successfully')
    } catch (error) {
      console.error('Failed to register device token:', error)
    }
  })

  PushNotifications.addListener('registrationError', (error) => {
    console.error('Push registration error:', error)
  })

  PushNotifications.addListener(
    'pushNotificationReceived',
    (notification: PushNotificationSchema) => {
      console.log('Push received (foreground):', notification)
    }
  )

  PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action: ActionPerformed) => {
      console.log('Push action performed:', action)
    }
  )

  await PushNotifications.register()
  return true
}

// A persisted device token is the closest proxy for "this device is
// registered for push" — it is cleared on unregister/logout.
export const isPushNotificationsEnabled = async (): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    return false
  }

  const permStatus = await PushNotifications.checkPermissions()
  if (permStatus.receive !== 'granted') {
    return false
  }

  return !!(await loadPersistedToken())
}

export const unregisterPushNotifications = async (accessToken: string): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    return
  }

  const token = await loadPersistedToken()

  try {
    if (token) {
      await apiInstance({
        url: '/notifications/device-tokens/unregister/',
        method: 'POST',
        headers: { authorization: `Bearer ${accessToken}` },
        data: { token }
      })
      console.log('Device token unregistered')
    }
  } catch (error) {
    console.error('Failed to unregister device token:', error)
  } finally {
    await persistToken(null)
    await PushNotifications.removeAllListeners()
    await PushNotifications.unregister()
  }
}

export const getCurrentToken = (): string | null => currentToken
